import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';

const AddCustomerModal = ({ open, onClose, onCustomerAdded, garageId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [vehicleReg, setVehicleReg] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [mobile, setMobile] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);
  const [vehicleLookupError, setVehicleLookupError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Real-time validation for individual fields
  const validateCustomerName = (value) => {
    if (!value.trim()) {
      return 'Customer name is required';
    }
    return '';
  };

  const validateMobile = (value) => {
    if (!value.trim()) {
      return 'Mobile number is required';
    }
    const cleanMobile = value.replace(/\s/g, '');
    if (!/^07\d{9}$/.test(cleanMobile)) {
      return 'Please enter a valid UK mobile number (e.g. 07863723648)';
    }
    return '';
  };

  // Check if all fields are valid (for enabling/disabling submit button)
  const isFormValid = () => {
    return selectedVehicle && 
           customerName.trim() && 
           mobile.trim() && 
           /^07\d{9}$/.test(mobile.replace(/\s/g, ''));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedVehicle) {
      newErrors.vehicle = 'Please search and select a vehicle';
    }
    if (!customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    }
    if (!mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else {
      // UK mobile validation: 11 digits starting with 07
      const cleanMobile = mobile.replace(/\s/g, '');
      if (!/^07\d{9}$/.test(cleanMobile)) {
        newErrors.mobile = 'Please enter a valid UK mobile number (e.g. 07863723648)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVehicleLookup = async () => {
    if (!vehicleReg.trim()) return;

    setVehicleLookupLoading(true);
    setVehicleLookupError('');
    setSelectedVehicle(null);

    try {
      const response = await apiService.lookupVehicle(vehicleReg.trim().toUpperCase(), {
        refreshMot: true,
      });
      if (response.data) {
        setSelectedVehicle(response.data);
        setVehicleLookupError('');
      } else {
        setVehicleLookupError('Vehicle not found. Please check the registration number.');
      }
    } catch (error) {
      console.error('Vehicle lookup error:', error);
      
      // Extract meaningful error message from backend response
      let errorMessage = 'Failed to lookup vehicle. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.detail) {
        errorMessage = error.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setVehicleLookupError(errorMessage);
    } finally {
      setVehicleLookupLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitLoading(true);
    try {
      const customerData = {
        vehicle_id: selectedVehicle.id,
        customer_name: customerName.trim(),
        mobile: mobile.trim()
      };

      const response = await apiService.createGarageCustomer(garageId, customerData);
      
      // Notify parent component with the created customer ID
      onCustomerAdded({
        id: response.data.id,  // Include the UUID returned from the API
        vehicle: selectedVehicle,
        customer_name: customerName.trim(),
        mobile: mobile.trim()
      });
      
      handleClose();
    } catch (error) {
      console.error('Error creating customer:', error);
      
      // Extract meaningful error message from backend response
      let errorMessage = 'Failed to add customer. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.detail) {
        errorMessage = error.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Set appropriate error field based on the error message
      if (errorMessage.toLowerCase().includes('already associated') || 
          errorMessage.toLowerCase().includes('vehicle already') ||
          errorMessage.toLowerCase().includes('already exists')) {
        setErrors({ vehicle: errorMessage });
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    setVehicleReg('');
    setCustomerName('');
    setMobile('');
    setSelectedVehicle(null);
    setVehicleLookupError('');
    setErrors({});
    setTouched({});
    onClose();
  };

  const handleVehicleRegChange = (e) => {
    const value = e.target.value.toUpperCase();
    setVehicleReg(value);
    if (selectedVehicle) {
      setSelectedVehicle(null);
    }
    if (errors.vehicle) {
      setErrors(prev => ({ ...prev, vehicle: '' }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 3 },
          m: { xs: 0, md: 2 },
          height: { xs: '100vh', md: 'auto' },
          maxHeight: { md: '90vh' }
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6" component="span" fontWeight="600">
          Add New Customer
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        pt: 4, 
        pb: 3,
        px: 3,
        flex: 1, 
        overflow: 'auto' 
      }}>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Vehicle Search */}
          <Box>
            <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 2 }}>
              Vehicle Registration
            </Typography>
            
            <TextField
              fullWidth
              label="Registration Number"
              value={vehicleReg}
              onChange={handleVehicleRegChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && vehicleReg.trim() && !vehicleLookupLoading) {
                  handleVehicleLookup();
                }
              }}
              placeholder="e.g. AB12 CDE"
              error={!!errors.vehicle || !!vehicleLookupError}
              helperText={errors.vehicle || vehicleLookupError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CarIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={handleVehicleLookup}
                      disabled={!vehicleReg.trim() || vehicleLookupLoading}
                      variant="contained"
                      size="small"
                      sx={{
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        minWidth: 'auto',
                        px: 2
                      }}
                    >
                      {vehicleLookupLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SearchIcon sx={{ fontSize: 18 }} />
                      )}
                    </Button>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            {/* Selected Vehicle Display */}
            {selectedVehicle && (
              <Card 
                variant="outlined" 
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(46, 125, 50, 0.1)' 
                    : 'rgba(76, 175, 80, 0.05)',
                  borderColor: theme.palette.mode === 'dark' 
                    ? 'rgba(76, 175, 80, 0.3)' 
                    : 'rgba(76, 175, 80, 0.2)'
                }}
              >
                <CardContent sx={{ pt: 2, pb: '16px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CarIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      {selectedVehicle.registration_number}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    '& .MuiChip-root': {
                      fontSize: '0.75rem',
                      height: 28
                    }
                  }}>
                    {selectedVehicle.make && (
                      <Chip 
                        label={`${selectedVehicle.make}${selectedVehicle.model ? ` ${selectedVehicle.model}` : ''}`}
                        variant="outlined" 
                        size="small" 
                        color="primary"
                      />
                    )}
                    {selectedVehicle.year && (
                      <Chip 
                        label={selectedVehicle.year} 
                        variant="outlined" 
                        size="small"
                      />
                    )}
                    {selectedVehicle.colour && (
                      <Chip 
                        label={selectedVehicle.colour} 
                        variant="outlined" 
                        size="small"
                      />
                    )}
                    {selectedVehicle.fuel_type && (
                      <Chip 
                        label={selectedVehicle.fuel_type} 
                        variant="outlined" 
                        size="small"
                      />
                    )}
                    {selectedVehicle.mot_expiry_date && (
                      <Chip 
                        label={`MOT expires on ${new Date(selectedVehicle.mot_expiry_date).toLocaleDateString('en-GB')}`}
                        variant="outlined" 
                        size="small"
                        color="error"
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Customer Details - Only show after successful vehicle lookup */}
          {selectedVehicle && (
            <Box>
              <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 2 }}>
                Customer Details
              </Typography>
              
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  placeholder="Enter customer name"
                  value={customerName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomerName(value);
                    
                    // Mark as touched and validate on change
                    setTouched(prev => ({ ...prev, customerName: true }));
                    const validationError = validateCustomerName(value);
                    setErrors(prev => ({ ...prev, customerName: validationError }));
                  }}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, customerName: true }));
                    const validationError = validateCustomerName(customerName);
                    setErrors(prev => ({ ...prev, customerName: validationError }));
                  }}
                  error={touched.customerName && !!errors.customerName}
                  helperText={touched.customerName && errors.customerName ? errors.customerName : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    )
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Mobile Number"
                  placeholder="e.g. 07863723648"
                  value={mobile}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMobile(value);
                    
                    // Mark as touched and validate on change
                    setTouched(prev => ({ ...prev, mobile: true }));
                    const validationError = validateMobile(value);
                    setErrors(prev => ({ ...prev, mobile: validationError }));
                  }}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, mobile: true }));
                    const validationError = validateMobile(mobile);
                    setErrors(prev => ({ ...prev, mobile: validationError }));
                  }}
                  error={touched.mobile && !!errors.mobile}
                  helperText={touched.mobile && errors.mobile ? errors.mobile : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Stack>
            </Box>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
              {errors.submit}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 1
      }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 100
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={submitLoading || !isFormValid()}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 100
          }}
        >
          {submitLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Adding...
            </>
          ) : (
            'Add Customer'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCustomerModal;